import { FOCUS_RING, ModalDialog } from '@amelio/ui-library';
import { useMemo } from 'react';

import { AssignedEmployeesList } from '../employees/AssignedEmployeesList';
import { XIcon } from '../icons';

import type { CompetencyProfile } from '@/types/competency/CompetencyProfile';
import type { User } from '@/types/directory/User';

import {
    useGetProfileAssignments,
    useUnassignProfile,
} from '@/api/competency-services/profile-assignment';
import OverlayLoader from '@/components/OverlayLoader/OverlayLoader';
import { useAmelioTranslations } from '@/hooks';
import { useSelectedAppLanguage } from '@/hooks/company/useSelectedAppLanguage';
import { useError } from '@/hooks/utils/useError';
import { fullName, hasName } from '@/pages/Competencies/components/employees/personDisplay';
import { showSuccessToast } from '@/plugins/Toast';
import { getTextByLanguage } from '@/utils/localization';

type ProfileAssignedEmployeesModalProps = {
    /** The tab mounts this dialog only while a profile is being inspected, so it opens by default. */
    isOpen?: boolean;
    profile: CompetencyProfile;
    onClose: () => void;
};

/**
 * Drill-in for a profile's "N employés" badge: lists the currently assigned
 * employees (identities resolved from the directory) and lets each one be
 * unassigned in place. The list refreshes itself through the assignment
 * read-model invalidation triggered by the unassign mutation.
 */
export const ProfileAssignedEmployeesModal = ({
    isOpen = true,
    profile,
    onClose,
}: ProfileAssignedEmployeesModalProps) => {
    const { t } = useAmelioTranslations();
    const language = useSelectedAppLanguage();
    const { handleError } = useError();

    const {
        data: assignments,
        isLoading,
        isFetching,
        isError,
    } = useGetProfileAssignments(profile.id);
    const { mutateAsync: unassignProfile, isPending: isUnassigning } = useUnassignProfile();

    // A just-unassigned employee stays in the rendered list until the assignments
    // refetch lands; freezing the buttons through that window prevents a second
    // unassign POST on an already-removed row.
    const isRowActionLocked = isUnassigning || isFetching;

    const employeeIds = useMemo(
        () => (assignments ?? []).map((assignment) => assignment.employeeId),
        [assignments],
    );

    const onUnassign = async (user: User) => {
        try {
            await unassignProfile({ profileId: profile.id, employeeIds: [user.id] });

            showSuccessToast({
                message: t('competencies.profiles.assignedEmployees.toast.unassigned', {
                    name: hasName(user) ? fullName(user) : t('common.anonymous'),
                }),
            });
        } catch (error) {
            handleError({ error });
        }
    };

    return (
        <ModalDialog
            isOpen={isOpen}
            onClose={onClose}
            closeAriaLabel={t('common.close')}
            title={t('competencies.profiles.assignedEmployees.title', {
                name: getTextByLanguage(profile.name, language),
            })}
            subtitle={t('competencies.profiles.assignedEmployees.subtitle', {
                count: employeeIds.length,
            })}
            size={'md'}
            backdropBlur
            dataTestId={'profile-assigned-employees-modal'}
        >
            {isLoading ? (
                <div className={'flex items-center justify-center py-12'}>
                    <OverlayLoader isLoading={isLoading} />
                </div>
            ) : isError ? (
                <p className={'text-error py-10 text-center text-base'}>
                    {t('competencies.assignedProfilesModal.employeesError')}
                </p>
            ) : (
                <AssignedEmployeesList
                    employeeIds={employeeIds}
                    cacheIdentifier={profile.id}
                    renderRowAction={(user) => (
                        <button
                            type={'button'}
                            onClick={() => void onUnassign(user)}
                            disabled={isRowActionLocked}
                            className={`${FOCUS_RING} hover:border-error/40 hover:bg-error/10 hover:text-error border-base-100 text-base-500 flex flex-shrink-0 items-center gap-1.5 border px-3 py-1.5 text-xs font-medium transition-colors disabled:opacity-50`}
                            data-testid={`profile-unassign-${profile.id}-${user.id}`}
                        >
                            <XIcon className={'h-3.5 w-3.5'} />

                            {t('competencies.profiles.assignedEmployees.unassign')}
                        </button>
                    )}
                />
            )}
        </ModalDialog>
    );
};
